import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTheme } from "../context/ThemeContext";
import { toast } from "react-hot-toast";
import { FaArrowLeft, FaEdit, FaSpinner, FaCheck, FaTimes, FaComment } from "react-icons/fa";
import axiosInstance from "../utils/axiosConfig";
import Loader from "../components/Loader";

const PaperViewScreen = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isDarkMode } = useTheme();
  const [paper, setPaper] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchPaperDetails();
  }, [id]);

  const fetchPaperDetails = async () => {
    try {
      setLoading(true);
      const response = await axiosInstance.get(`/api/faculty/papers/${id}`);
      setPaper(response.data);
    } catch (error) {
      console.error("Error fetching paper details:", error);
      toast.error(error.response?.data?.message || "Failed to load paper details");
      setError(error.response?.data?.message || "Failed to load paper details");
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadgeClass = (status) => {
    if (isDarkMode) {
      switch (status) {
        case 'approved':
          return 'bg-green-400/10 text-green-400';
        case 'rejected':
          return 'bg-red-400/10 text-red-400';
        case 'pending':
        default:
          return 'bg-yellow-400/10 text-yellow-400';
      }
    } else {
      switch (status) {
        case 'approved':
          return 'bg-green-100 text-green-700';
        case 'rejected':
          return 'bg-red-100 text-red-700';
        case 'pending':
        default:
          return 'bg-yellow-100 text-yellow-700';
      }
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'approved':
        return <FaCheck className={isDarkMode ? 'text-green-400' : 'text-green-700'} />;
      case 'rejected':
        return <FaTimes className={isDarkMode ? 'text-red-400' : 'text-red-700'} />;
      case 'pending':
      default:
        return <FaSpinner className={isDarkMode ? 'text-yellow-400' : 'text-yellow-700'} />;
    }
  };

  if (loading) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <Loader size="large" text="Loading paper details..." />
      </div>
    );
  }

  if (error) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-red-500 mb-4">{error}</div>
        <button
          onClick={() => navigate('/faculty/dashboard')}
          className={`px-4 py-2 rounded-md flex items-center ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
        >
          <FaArrowLeft className="mr-2" /> Back to Dashboard
        </button>
      </div>
    );
  }

  if (!paper) {
    return (
      <div className={`min-h-screen pt-24 pb-12 flex flex-col items-center justify-center ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
        <div className="text-lg mb-4">Paper not found</div>
        <button
          onClick={() => navigate('/faculty/dashboard')}
          className={`px-4 py-2 rounded-md flex items-center ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
        >
          <FaArrowLeft className="mr-2" /> Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div className={`min-h-screen pt-24 pb-12 ${isDarkMode ? 'bg-gray-900 text-white' : 'bg-gray-50 text-gray-900'}`}>
      <div className="container mx-auto px-4 md:px-6">
        <div className="mb-6 flex items-center gap-4">
          <button
            onClick={() => navigate('/faculty/dashboard')}
            className={`p-2 rounded-full ${isDarkMode ? 'bg-gray-800 hover:bg-gray-700' : 'bg-white hover:bg-gray-100'}`}
          >
            <FaArrowLeft />
          </button>
          <h1 className="text-2xl font-bold">View Paper</h1>
          {paper.status === 'pending' && (
            <button
              onClick={() => navigate(`/faculty/papers/edit/${paper._id}`)}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors inline-flex items-center ml-auto"
            >
              <FaEdit className="mr-2" /> Edit Paper
            </button>
          )}
        </div>

        <div className={`rounded-xl p-6 mb-8 ${isDarkMode ? 'bg-gray-800' : 'bg-white'} shadow-lg`}>
          <div className="mb-6">
            <div className="flex justify-between items-start flex-wrap gap-4">
              <div>
                <h2 className="text-2xl font-bold">{paper.title}</h2>
                <div className="mt-2 text-sm">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Created: </span>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{new Date(paper.createdAt).toLocaleDateString()}</span>
                </div>
                <div className="mt-1 text-sm">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Department: </span>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{paper.department?.name}</span>
                </div>
                <div className="mt-1 text-sm">
                  <span className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>Subject: </span>
                  <span className={isDarkMode ? 'text-gray-300' : 'text-gray-700'}>{paper.subject?.name}</span>
                </div>
              </div>
              <div className="flex flex-col gap-2">
                <div className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-2 ${getStatusBadgeClass(paper.status)}`}>
                  {getStatusIcon(paper.status)}
                  <span>
                    {paper.status === 'approved' ? 'Approved' : 
                     paper.status === 'rejected' ? 'Rejected' : 'Pending Review'}
                  </span>
                </div>
                <div className={`px-3 py-1 rounded-full text-sm font-medium inline-flex items-center gap-2 ${
                  isDarkMode ? 'bg-blue-900/30 text-blue-400' : 'bg-blue-100 text-blue-700'
                }`}>
                  {paper.questions.length} Questions
                </div>
              </div>
            </div>
            {paper.description && (
              <p className={`mt-4 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                {paper.description}
              </p>
            )}
          </div>

          {/* Show reviewer feedback if available */}
          {(paper.status === 'approved' || paper.status === 'rejected') && paper.reviewComment && (
            <div className={`p-4 mb-6 rounded-lg ${
              paper.status === 'approved'
                ? isDarkMode ? 'bg-green-900/20 border border-green-800' : 'bg-green-50 border border-green-200'
                : isDarkMode ? 'bg-red-900/20 border border-red-800' : 'bg-red-50 border border-red-200'
            }`}>
              <div className="flex items-center gap-2 mb-2">
                <FaComment className={
                  paper.status === 'approved'
                    ? isDarkMode ? 'text-green-400' : 'text-green-600'
                    : isDarkMode ? 'text-red-400' : 'text-red-600'
                } />
                <h3 className="font-medium">Reviewer Feedback</h3>
              </div>
              <p className={`${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>{paper.reviewComment}</p>
            </div>
          )}

          <div className="my-8">
            <h3 className="text-xl font-semibold mb-4">Questions</h3>
            <div className="space-y-6">
              {paper.questions.map((question, index) => (
                <div 
                  key={question._id || index}
                  className={`p-4 rounded-lg ${isDarkMode ? 'bg-gray-750' : 'bg-gray-50'}`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <div className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>
                      Question #{index + 1}
                    </div>
                  </div>
                  <p className="mb-4">{question.text}</p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {question.options?.map((option, optIndex) => (
                      <div 
                        key={optIndex}
                        className={`p-3 rounded-md border flex items-start ${
                          option.isCorrect
                            ? isDarkMode 
                              ? 'bg-green-900/20 border-green-800 text-green-400' 
                              : 'bg-green-50 border-green-200 text-green-800'
                            : isDarkMode
                              ? 'bg-gray-800 border-gray-700'
                              : 'bg-white border-gray-200'
                        }`}
                      >
                        <div className={`w-6 h-6 flex items-center justify-center rounded-full mr-2 ${
                          option.isCorrect
                            ? isDarkMode ? 'bg-green-900 text-green-400' : 'bg-green-100 text-green-800'
                            : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-100 text-gray-700'
                        }`}>
                          {['A', 'B', 'C', 'D', 'E', 'F'][optIndex]}
                        </div>
                        <div className="flex-1">
                          {option.text}
                        </div>
                        {option.isCorrect && (
                          <FaCheck className={isDarkMode ? 'text-green-400' : 'text-green-600'} />
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaperViewScreen; 